/**
 * Client-side classification of Review findings as "current" or "inherited"
 * relative to a selected submission.
 *
 * A finding is "current" if ANY of its documents belong to the selected
 * submission. Otherwise it is "inherited" — it exists in documents from
 * prior submissions that the current submission did not change.
 *
 * A2F-443: Inherited findings do NOT count toward the selected submission's
 * completion metrics.
 */

import type { ExplorerDocument } from '@/types/api';

/** Map from document ID to submission ID. */
export type DocSubmissionMap = Map<string, string>;

/** Build a docId → submissionId lookup from explorer documents. */
export function buildDocSubmissionMap(documents: ExplorerDocument[]): DocSubmissionMap {
  const map = new Map<string, string>();
  for (const doc of documents) {
    map.set(doc.id, doc.submissionId);
  }
  return map;
}

/** Get the set of document IDs belonging to a specific submission. */
export function getSubmissionDocIds(
  docSubMap: DocSubmissionMap,
  submissionId: string,
): Set<string> {
  const ids = new Set<string>();
  for (const [docId, subId] of docSubMap) {
    if (subId === submissionId) ids.add(docId);
  }
  return ids;
}

/**
 * Three-tier provenance classification for a single finding.
 *
 * - **new**: Finding only exists in current-submission documents.
 * - **pre-existing**: Finding exists in both current and inherited documents.
 * - **inherited**: Finding only exists in inherited documents.
 *
 * NOTE: For conflicting abbreviations, use the per-definition provenance
 * check in AbbreviationsPage (defSurvivesWithoutCurrentSubmission) instead
 * of this generic utility. Conflicts require checking each definition's
 * source documents independently, not the finding's aggregate document list.
 */
export type FindingTier = 'new' | 'pre-existing' | 'inherited';

export function classifyFindingTier(
  finding: { documents?: string[]; tier?: FindingTier | null },
  documentMap: Record<string, string>,
  currentDocIds: Set<string>,
): FindingTier {
  // A2F-566: server attaches `tier` once in consistency_service.get_consistency
  // and all consumers prefer that canonical value over re-deriving. Local
  // derivation stays as a transitional fallback for payloads where the
  // server hasn't populated `tier` yet (e.g. cached responses during rollout,
  // or future surfaces that call the raw scan endpoints directly). Removal
  // of the fallback is tracked in A2F-568.
  if (finding.tier) return finding.tier;

  const docs = finding.documents ?? [];
  let hasCurrentDoc = false;
  let hasInheritedDoc = false;

  for (const filename of docs) {
    const docId = documentMap[filename];
    if (docId != null && currentDocIds.has(docId)) {
      hasCurrentDoc = true;
    } else {
      hasInheritedDoc = true;
    }
    if (hasCurrentDoc && hasInheritedDoc) break;
  }

  if (!hasCurrentDoc) return 'inherited';
  if (hasInheritedDoc) return 'pre-existing';
  return 'new';
}

/**
 * A2F-487: three-bucket count of findings, matching the ScopeFilterSelect
 * options exactly. Each bucket is cumulative — `thisSubAndPrior` includes
 * `thisSub`, and `allScopes` includes both.
 *
 * Used by `<ScopeBreakdownCard>` on the dashboard's Review tiles and by
 * Review pages that don't already have tier-tagged item lists handy.
 */
export interface FindingScopeBreakdown {
  thisSub: number;
  thisSubAndPrior: number;
  allScopes: number;
}

export function classifyFindingsByScope<T extends { documents?: string[]; tier?: FindingTier | null }>(
  findings: T[],
  documentMap: Record<string, string>,
  currentDocIds: Set<string>,
  filterFn?: (finding: T) => boolean,
): FindingScopeBreakdown {
  let thisSub = 0;
  let preExisting = 0;
  let inherited = 0;

  for (const finding of findings) {
    if (filterFn && !filterFn(finding)) continue;
    const tier = classifyFindingTier(finding, documentMap, currentDocIds);
    if (tier === 'new') thisSub++;
    else if (tier === 'pre-existing') preExisting++;
    else inherited++;
  }

  return {
    thisSub,
    thisSubAndPrior: thisSub + preExisting,
    allScopes: thisSub + preExisting + inherited,
  };
}

/**
 * A2F-487: three-bucket count of pre-tiered items. Cheaper than
 * `classifyFindingsByScope` when the page already has a `tieredItems`
 * list (which all 5 Review pages do).
 */
export function countByScope<T extends { tier: FindingTier }>(
  items: T[],
): FindingScopeBreakdown {
  let thisSub = 0;
  let preExisting = 0;
  let inherited = 0;

  for (const item of items) {
    if (item.tier === 'new') thisSub++;
    else if (item.tier === 'pre-existing') preExisting++;
    else inherited++;
  }

  return {
    thisSub,
    thisSubAndPrior: thisSub + preExisting,
    allScopes: thisSub + preExisting + inherited,
  };
}
